<template>
  <header class="sticky top-0 z-50">
    <div class="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/60 dark:border-gray-700/60" />

    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-14">
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-linear-to-br from-primary-500 to-primary-700 shadow-sm shadow-primary-200 dark:shadow-primary-900/30">
            <Icon
              icon="heroicons:chart-bar"
              class="w-4.5 h-4.5 text-white"
            />
          </div>
          <h1 class="text-lg font-bold text-gray-900 dark:text-gray-100 tracking-tight">
            Halo 统计分析
          </h1>
        </div>

        <div class="flex items-center gap-1">
          <nav class="hidden sm:flex items-center bg-gray-100/80 dark:bg-gray-800/80 rounded-lg p-0.5">
            <router-link
              v-for="item in navItems"
              :key="item.path"
              :to="item.path"
              class="relative px-3.5 py-1.5 text-sm font-medium rounded-md transition-all duration-200"
              :class="route.path === item.path
                ? 'bg-white dark:bg-gray-700 text-primary-700 dark:text-primary-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
            >
              {{ item.label }}
            </router-link>
          </nav>
          <button
            class="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="切换主题"
            @click="toggleTheme"
          >
            <Icon
              v-if="themeMode === 'system'"
              icon="heroicons:computer-desktop"
              class="w-5 h-5"
            />
            <Icon
              v-else-if="themeMode === 'light'"
              icon="heroicons:sun"
              class="w-5 h-5"
            />
            <Icon
              v-else
              icon="heroicons:moon"
              class="w-5 h-5"
            />
          </button>

          <button
            class="sm:hidden flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="打开菜单"
            @click="toggleMobileMenu($event)"
          >
            <Icon
              v-if="!mobileMenuOpen"
              icon="heroicons:bars-3"
              class="w-5 h-5 transition-transform duration-200"
            />
            <Icon
              v-else
              icon="heroicons:x-mark"
              class="w-5 h-5 transition-transform duration-200 rotate-90"
            />
          </button>
        </div>
      </div>
    </div>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="mobileMenuOpen"
        class="relative sm:hidden border-t border-gray-200/60 dark:border-gray-700/60"
      >
        <div class="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl">
          <div class="max-w-7xl mx-auto px-4 py-2.5 space-y-0.5">
            <router-link
              v-for="item in navItems"
              :key="item.path"
              :to="item.path"
              class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              :class="route.path === item.path
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'"
              @click="(e) => { e.stopPropagation(); mobileMenuOpen = false }"
            >
              <span
                class="w-1 h-1 rounded-full"
                :class="route.path === item.path ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'"
              />
              {{ item.label }}
            </router-link>
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { Icon } from '@iconify/vue'
import { useTheme } from '@/composables/useTheme'

const route = useRoute()
const { themeMode, toggleTheme } = useTheme()

const mobileMenuOpen = ref(false)

const navItems = [
  { path: '/', label: '数据概览' },
  { path: '/ai-analysis', label: '风格分析' },
  { path: '/settings', label: '配置' },
]

function toggleMobileMenu(e: MouseEvent) {
  e.stopPropagation()
  mobileMenuOpen.value = !mobileMenuOpen.value
}

function handleResize() {
  if (window.innerWidth >= 640) {
    mobileMenuOpen.value = false
  }
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (!target.closest('header')) {
    mobileMenuOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleClickOutside)
})
</script>
